import { useState, useEffect } from 'react';
    import {
      collection,
      query,
      orderBy,
      onSnapshot,
      addDoc,
      deleteDoc,
      doc,
      Timestamp,
      updateDoc,
      where,
      getDocs,
    } from 'firebase/firestore';
    import { db } from '../../../utils/init-firebase'; // Adjust this path based on new structure
    import { COLLECTION_NAMES, MetricName, Department } from '../types';
import { useAuth } from '../../../contexts/AuthContext';

    /**
     * Hook for managing Admin Panel data, including Metric Names and Departments.
     */
    export function useAdmin() {
      const { currentUser } = useAuth();
      const [metricNames, setMetricNames] = useState<MetricName[]>([]);
      const [departments, setDepartments] = useState<Department[]>([]);
      const [loading, setLoading] = useState(true);
      const [departmentsLoading, setDepartmentsLoading] = useState(true);

      // Load metric names with a real-time listener
      useEffect(() => {
        const metricNamesQuery = query(
          collection(db, COLLECTION_NAMES.METRIC_NAMES),
          orderBy('name', 'asc')
        );

        const unsubscribe = onSnapshot(
          metricNamesQuery,
          (snapshot) => {
            const namesData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt || Timestamp.now(),
              } as MetricName;
            });
            
            // Deduplicate client-side just in case
            const uniqueByName = Array.from(
              new Map(namesData.map((item) => [item.name?.toLowerCase().trim(), item])).values()
            );
            
            uniqueByName.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setMetricNames(uniqueByName);
            setLoading(false);
          },
          (error) => {
            console.error('Error loading metric names:', error);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      }, []);

      // Load departments with a real-time listener (excluding deleted)
      useEffect(() => {
        const departmentsQuery = query(
          collection(db, COLLECTION_NAMES.DEPARTMENTS),
          orderBy('name', 'asc')
        );

        const unsubscribe = onSnapshot(
          departmentsQuery,
          (snapshot) => {
            const departmentsData = snapshot.docs
              .map((doc) => {
                const data = doc.data();
                return {
                  id: doc.id,
                  name: data.name || '',
                  createdAt: data.createdAt,
                  updatedAt: data.updatedAt,
                  createdBy: data.createdBy,
                  deletedAt: data.deletedAt,
                } as Department;
              })
              // Filter out soft-deleted departments
              .filter((dept) => !dept.deletedAt);
            
            setDepartments(departmentsData);
            setDepartmentsLoading(false);
          },
          (error) => {
            console.error('Error loading departments:', error);
            setDepartmentsLoading(false);
          }
        );

        return () => unsubscribe();
      }, []);

      /**
       * Adds a new metric name to the database.
       * @param name The new metric name to add.
       */
      const addMetricName = async (name: string) => {
        if (!name.trim()) {
          throw new Error('Metric name cannot be empty.');
        }
        if (!currentUser?.uid) {
          throw new Error('User must be authenticated to add a metric.');
        }

        // Check for duplicates
        const lowerCaseName = name.toLowerCase().trim();
        if (metricNames.some(m => m.name.toLowerCase().trim() === lowerCaseName)) {
          throw new Error('This metric name already exists.');
        }

        try {
          await addDoc(collection(db, COLLECTION_NAMES.METRIC_NAMES), {
            name: name.trim(),
            createdAt: Timestamp.now(),
            createdBy: currentUser.uid,
          });
        } catch (error) {
          console.error('Error adding metric name:', error);
          throw new Error('Failed to add metric name.');
        }
      };

      /**
       * Deletes a metric name from the database.
       * @param id The ID of the metric name to delete.
       */
      const deleteMetricName = async (id: string) => {
        try {
          const docRef = doc(db, COLLECTION_NAMES.METRIC_NAMES, id);
          await deleteDoc(docRef);
        } catch (error) {
          console.error('Error deleting metric name:', error);
          throw new Error('Failed to delete metric name.');
        }
      };

      /**
       * Adds a new department to the database.
       * @param name The new department name to add.
       */
      const addDepartment = async (name: string) => {
        if (!name.trim()) {
          throw new Error('Department name cannot be empty.');
        }
        if (!currentUser?.uid) {
          throw new Error('User must be authenticated to add a department.');
        }

        // Check for duplicates (case-insensitive)
        const lowerCaseName = name.toLowerCase().trim();
        if (departments.some(d => d.name.toLowerCase().trim() === lowerCaseName)) {
          throw new Error('This department name already exists.');
        }

        try {
          await addDoc(collection(db, COLLECTION_NAMES.DEPARTMENTS), {
            name: name.trim(),
            createdAt: Timestamp.now(),
            createdBy: currentUser.uid,
          });
        } catch (error) {
          console.error('Error adding department:', error);
          throw new Error('Failed to add department.');
        }
      };

      /**
       * Checks if a department is in use across the application.
       * @param id The ID of the department to check.
       * @returns Usage information including counts per entity type.
       */
      const checkDepartmentUsage = async (id: string): Promise<{
        inUse: boolean;
        usage: {
          rocks: number;
          issues: number;
          vtos: number;
          l10Notes: number;
        };
      }> => {
        try {
          // Query all collections that might use this department
          const [rocksSnapshot, issuesSnapshot, vtosSnapshot, notesSnapshot] = await Promise.all([
            getDocs(query(collection(db, COLLECTION_NAMES.ROCKS), where('departmentId', '==', id))),
            getDocs(query(collection(db, COLLECTION_NAMES.ISSUES), where('departmentId', '==', id))),
            getDocs(query(collection(db, COLLECTION_NAMES.VTO), where('departmentId', '==', id))),
            getDocs(query(collection(db, COLLECTION_NAMES.L10_NOTES))),
          ]);

          // Filter L10 notes client-side for array contains
          const notesWithDept = notesSnapshot.docs.filter((doc) => {
            const data = doc.data();
            return data.assignedDepartments?.includes?.(id);
          });

          const usage = {
            rocks: rocksSnapshot.size,
            issues: issuesSnapshot.size,
            vtos: vtosSnapshot.size,
            l10Notes: notesWithDept.length,
          };

          return {
            inUse: usage.rocks > 0 || usage.issues > 0 || usage.vtos > 0 || usage.l10Notes > 0,
            usage,
          };
        } catch (error) {
          console.error('Error checking department usage:', error);
          throw new Error('Failed to check department usage.');
        }
      };

      /**
       * Soft deletes a department from the database (sets deletedAt timestamp).
       * @param id The ID of the department to delete.
       */
      const deleteDepartment = async (id: string) => {
        try {
          const docRef = doc(db, COLLECTION_NAMES.DEPARTMENTS, id);
          await updateDoc(docRef, {
            deletedAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        } catch (error) {
          console.error('Error deleting department:', error);
          throw new Error('Failed to delete department.');
        }
      };

      /**
       * Restores a soft-deleted department.
       * @param id The ID of the department to restore.
       */
      const restoreDepartment = async (id: string) => {
        try {
          const docRef = doc(db, COLLECTION_NAMES.DEPARTMENTS, id);
          await updateDoc(docRef, {
            deletedAt: null,
            updatedAt: Timestamp.now(),
          });
        } catch (error) {
          console.error('Error restoring department:', error);
          throw new Error('Failed to restore department.');
        }
      };

      /**
       * Gets all deleted departments for restore functionality.
       * @returns Array of deleted departments.
       */
      const getDeletedDepartments = async (): Promise<Department[]> => {
        try {
          const departmentsQuery = query(
            collection(db, COLLECTION_NAMES.DEPARTMENTS),
            orderBy('name', 'asc')
          );

          const snapshot = await getDocs(departmentsQuery);
          const deletedDepartments = snapshot.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                name: data.name || '',
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
                createdBy: data.createdBy,
                deletedAt: data.deletedAt,
              } as Department;
            })
            // Filter to only include deleted departments
            .filter((dept) => dept.deletedAt);

          return deletedDepartments;
        } catch (error) {
          console.error('Error loading deleted departments:', error);
          throw new Error('Failed to load deleted departments.');
        }
      };

      return {
        metricNames,
        departments,
        loading,
        departmentsLoading,
        addMetricName,
        deleteMetricName,
        addDepartment,
        deleteDepartment,
        checkDepartmentUsage,
        restoreDepartment,
        getDeletedDepartments,
      };
    }